import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * ErrorBoundary — contains render/runtime errors inside a subtree.
 * Prevents a single component crash from wiping the parent (e.g., admin dashboard white screen).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console so admins can share the trace
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError) {
      const label = this.props.label || "This section";
      return (
        <div
          data-testid="error-boundary"
          className="rounded-2xl border-2 border-fuchsia-300 bg-fuchsia-50 p-5"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-fuchsia-700 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-[Cormorant_Garamond] text-xl text-emerald-950 leading-tight">
                {label} hit an error
              </div>
              <div className="text-xs font-[Outfit] text-emerald-950/70 mt-1">
                We've kept the rest of the dashboard working. You can retry, refresh the page, or share this message with support:
              </div>
              <pre
                data-testid="error-boundary-message"
                className="mt-2 rounded-lg bg-white border border-fuchsia-200 p-2 text-[11px] font-mono text-fuchsia-800 whitespace-pre-wrap break-words max-h-32 overflow-auto"
              >{String(this.state.error?.message || this.state.error || "Unknown error")}</pre>
              <div className="mt-3 flex gap-2">
                <Button
                  onClick={this.reset}
                  data-testid="error-boundary-retry"
                  className="rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-9"
                >
                  <RefreshCw size={12} className="mr-1.5" /> Retry
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="rounded-full text-xs h-9"
                >
                  Full page refresh
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
